'use client';
import { useState, useCallback, useEffect } from 'react';
import { SIDE_MENU_BREAKPOINTS } from '@/shared/libs/side-menu-breakpoints';
import { SIDE_MENU_ATTRIBUTE } from '@/shared/libs/side-menu-attribute';

function readUserPreference(): boolean | null {
  if (typeof window === 'undefined') return null;
  const saved = sessionStorage.getItem('isSideMenuOpen');
  return saved ? saved === 'true' : null;
}

function computeMenuState(breakpoints: typeof SIDE_MENU_BREAKPOINTS, userPreference: boolean | null) {
  if (typeof window === 'undefined') {
    return { isViewMenu: false, isExpandedMenu: false, shouldBeOpen: false, isReady: false };
  }

  const width = window.innerWidth;
  const isView = width >= breakpoints.mobile;
  const isExpanded = width >= breakpoints.expanded;

  return {
    isViewMenu: isView,
    isExpandedMenu: isExpanded,
    shouldBeOpen: userPreference !== null ? userPreference : isExpanded,
    isReady: true,
  };
}

export const useViewMenu = (breakpoints = SIDE_MENU_BREAKPOINTS) => {
    const [userPreference, setUserPreference] = useState<boolean | null>(readUserPreference);
    const [menuState, setMenuState] = useState(() => computeMenuState(breakpoints, userPreference));

    const setMenuManually = useCallback((isOpen: boolean) => {
      setUserPreference(isOpen);
      sessionStorage.setItem('isSideMenuOpen', String(isOpen));
      document.documentElement.setAttribute(SIDE_MENU_ATTRIBUTE, String(isOpen));
    }, []);

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const updateMenuState = () => {
        setMenuState(computeMenuState(breakpoints, userPreference));
      };

      updateMenuState();
      window.addEventListener('resize', updateMenuState);
      return () => window.removeEventListener('resize', updateMenuState);
    }, [breakpoints.mobile, breakpoints.expanded, userPreference]);

    return {
      ...menuState,
      setMenuManually,
    };
  };
